/**
 * Notification channels (email/telegram) — Preferences card state.
 * Mirrors useAlertSubscriptions' conventions: PRO-gated query (402 →
 * locked), optimistic toggle with revert, memoized derives, stable
 * callbacks. Adds the telegram link-waiting mode: after startLink()
 * the channels query polls every pollIntervalMs (default 3s, max 2min)
 * until telegram.linked flips true.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/api/client";
import type { NotificationChannelsResponse } from "@/api/types";
import { useSubscription } from "@/billing/api";

// Shares the ["alerts", ...] namespace with ALERT_SUBSCRIPTIONS_KEY on purpose:
// a broad invalidate of alert state SHOULD refresh both.
export const NOTIFICATION_CHANNELS_KEY = ["alerts", "channels"] as const;
// Intentionally far below the link token's 900s TTL — if the user hasn't tapped
// Start within 2 minutes they've abandoned the flow; the card offers a fresh link.
const LINK_WAIT_MAX_MS = 120_000;

export function useNotificationChannels(pollIntervalMs = 3000) {
  const qc = useQueryClient();
  const { data: billing } = useSubscription();
  const tierLocked = billing?.tier === "free";
  const [linkWaiting, setLinkWaiting] = useState(false);
  const waitStartedAt = useRef(0);

  const query = useQuery({
    queryKey: NOTIFICATION_CHANNELS_KEY,
    queryFn: api.getNotificationChannels,
    enabled: !tierLocked,
    retry: false,
    staleTime: 60_000,
    refetchInterval: linkWaiting ? pollIntervalMs : false,
  });

  const apiLocked = query.error instanceof ApiError && query.error.status === 402;
  const locked = tierLocked || apiLocked;

  const email = useMemo(
    () => query.data?.channels.find((c) => c.kind === "email") ?? null,
    [query.data],
  );
  const telegram = useMemo(
    () => query.data?.channels.find((c) => c.kind === "telegram") ?? null,
    [query.data],
  );

  // Stop waiting once linked or after the max window.
  // Using useEffect (not mid-render setState) to avoid lint issues and
  // keep the stop-condition outside the render path.
  useEffect(() => {
    if (!linkWaiting) return;
    if (telegram?.linked) {
      setLinkWaiting(false);
      return;
    }
    const elapsed = Date.now() - waitStartedAt.current;
    if (elapsed > LINK_WAIT_MAX_MS) {
      setLinkWaiting(false);
      return;
    }
    // Schedule a check at the point where the max window will expire.
    const remaining = LINK_WAIT_MAX_MS - elapsed;
    const timer = setTimeout(() => setLinkWaiting(false), remaining);
    return () => clearTimeout(timer);
  }, [linkWaiting, telegram?.linked]);

  const toggleMutation = useMutation({
    mutationFn: ({ kind, enabled }: { kind: string; enabled: boolean }) =>
      api.setChannelEnabled(kind, enabled),
    onMutate: async ({ kind, enabled }) => {
      await qc.cancelQueries({ queryKey: NOTIFICATION_CHANNELS_KEY });
      const prev = qc.getQueryData<NotificationChannelsResponse>(NOTIFICATION_CHANNELS_KEY);
      qc.setQueryData<NotificationChannelsResponse>(NOTIFICATION_CHANNELS_KEY, (cur) =>
        cur ? { channels: cur.channels.map((c) =>
          c.kind === kind ? { ...c, enabled } : c) } : cur);
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev !== undefined) qc.setQueryData(NOTIFICATION_CHANNELS_KEY, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: NOTIFICATION_CHANNELS_KEY }),
  });
  const { mutateAsync: toggleAsync } = toggleMutation;

  const setEnabled = useCallback(
    (kind: string, enabled: boolean) => {
      if (locked) return Promise.resolve();
      return toggleAsync({ kind, enabled });
    },
    [locked, toggleAsync],
  );

  const startLink = useCallback(async () => {
    if (locked) return undefined;
    // Open the tab SYNCHRONOUSLY inside the user gesture (popup blockers
    // void window.open after an await), then navigate it once the deep
    // link arrives. If the browser still blocked it, the caller renders
    // the returned deep_link as a copyable fallback (Task 7).
    const win = window.open("about:blank", "_blank");
    try {
      const res = await api.linkTelegram();
      waitStartedAt.current = Date.now();
      setLinkWaiting(true);
      if (win) win.location.href = res.deep_link;
      return res.deep_link;
    } catch (e) {
      win?.close();
      throw e;
    }
  }, [locked]);

  const unlinkMutation = useMutation({
    mutationFn: api.unlinkTelegram,
    // An explicit unlink abandons any pending handshake — stop the poll loop immediately.
    onMutate: () => setLinkWaiting(false),
    onSettled: () => qc.invalidateQueries({ queryKey: NOTIFICATION_CHANNELS_KEY }),
  });
  const { mutateAsync: unlinkAsync } = unlinkMutation;
  const unlink = useCallback(() => unlinkAsync(), [unlinkAsync]);

  return {
    email, telegram, locked,
    loading: !tierLocked && query.isLoading,
    linkWaiting,
    cancelLinkWait: useCallback(() => setLinkWaiting(false), []),
    setEnabled, startLink, unlink,
  };
}
